import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { LogOut, Sparkles as SparkIcon, Zap, Target, Trophy } from "lucide-react";
import AppShell from "../components/AppShell";
import { useProfile } from "../context/ProfileContext";
import { api } from "../lib/api";
import { MASCOTS, STATE_META } from "../data/gameData";

const TABLES = Array.from({ length: 19 }, (_, i) => i + 2);
const MULTS = Array.from({ length: 20 }, (_, i) => i + 1);

export default function Profile() {
  const navigate = useNavigate();
  const { profile, logout, reduceMotion, setReduceMotion,
          soundMuted, setSoundMuted, readAloud, setReadAloud } = useProfile();
  const [tab, setTab] = useState("me");
  const [mastery, setMastery] = useState({ grid: {}, region_stars: {} });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (!profile) return;
    api.getMastery(profile.id).then(setMastery).catch(() => {});
    api.getSummary(profile.id).then(setSummary).catch(() => {});
  }, [profile?.id]); // eslint-disable-line

  if (!profile) return null;
  const mascot = MASCOTS[profile.mascot] || MASCOTS.lumi;

  return (
    <AppShell>
      {/* Header */}
      <div className="glass rounded-3xl p-5 mb-5 flex items-center gap-4" data-testid="profile-header">
        <div className="w-20 h-20 rounded-full overflow-hidden border-2 shrink-0" style={{ borderColor: mascot.color }}>
          <img src={mascot.img} alt={mascot.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="text-2xl font-black">{profile.name}</div>
          <div className="text-sm text-[#A8B2D1]">Level {profile.level} · Coach {mascot.name}</div>
          <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full rounded-full"
                 style={{ width: `${Math.round((profile.level_xp / profile.level_xp_needed) * 100)}%`,
                          background: "linear-gradient(90deg,#FF2A7A,#00E5FF)" }} />
          </div>
          <div className="text-[11px] text-[#A8B2D1] mt-1">{profile.level_xp} / {profile.level_xp_needed} XP</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <TabBtn active={tab === "me"} onClick={() => setTab("me")} tid="tab-me">My Progress</TabBtn>
        <TabBtn active={tab === "parent"} onClick={() => setTab("parent")} tid="tab-parent">Grown-ups</TabBtn>
      </div>

      {tab === "me" ? (
        <ChildView mastery={mastery} summary={summary} />
      ) : (
        <ParentView summary={summary} />
      )}

      {/* Settings */}
      <div className="glass rounded-3xl p-4 mt-5 space-y-4" data-testid="settings-card">
        <SettingToggle label="Sound & Music" desc="Upbeat music and answer sounds"
          on={!soundMuted} onToggle={() => setSoundMuted(!soundMuted)} tid="toggle-sound" color="#00E5FF" />
        <SettingToggle label="Read Equations Aloud" desc="Hear each question spoken"
          on={readAloud} onToggle={() => setReadAloud(!readAloud)} tid="toggle-readaloud" color="#FFD600" />
        <SettingToggle label="Reduce Motion" desc="Calmer animations"
          on={reduceMotion} onToggle={() => setReduceMotion(!reduceMotion)} tid="toggle-reduce-motion" color="#00FF66" />
      </div>

      <button
        data-testid="switch-player-btn"
        onClick={() => { logout(); navigate("/"); }}
        className="w-full btn-3d py-3 mt-4 glass text-white font-bold flex items-center justify-center gap-2">
        <LogOut size={18} /> Switch Player
      </button>
    </AppShell>
  );
}

function SettingToggle({ label, desc, on, onToggle, tid, color }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <div className="font-bold">{label}</div>
        <div className="text-xs text-[#A8B2D1]">{desc}</div>
      </div>
      <button
        data-testid={tid}
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={onToggle}
        className="w-14 h-8 rounded-full relative transition-colors shrink-0"
        style={{ background: on ? color : "rgba(255,255,255,0.15)" }}>
        <span className="absolute top-1 w-6 h-6 rounded-full bg-white transition-all"
              style={{ left: on ? "1.75rem" : "0.25rem" }} />
      </button>
    </div>
  );
}

function TabBtn({ active, onClick, children, tid }) {
  return (
    <button data-testid={tid} onClick={onClick}
      className="flex-1 py-2.5 rounded-2xl font-bold text-sm press border-2"
      style={active
        ? { background: "rgba(255,42,122,0.18)", borderColor: "#FF2A7A", color: "#fff" }
        : { background: "rgba(255,255,255,0.04)", borderColor: "transparent", color: "#A8B2D1" }}>
      {children}
    </button>
  );
}

function badgesFrom(summary, profile) {
  const c = summary?.state_counts || {};
  return [
    { id: "streak", label: `${profile.streak}-day streak`, icon: Zap, color: "#FF7A00", got: profile.streak >= 1 },
    { id: "mastered", label: `${c.mastered || 0} facts mastered`, icon: Trophy, color: "#00FF66", got: (c.mastered || 0) >= 1 },
    { id: "fluent", label: `${c.fluent || 0} facts fluent`, icon: Target, color: "#00E5FF", got: (c.fluent || 0) >= 1 },
    { id: "rounds", label: `${summary?.rounds_played || 0} rounds played`, icon: SparkIcon, color: "#FFD600", got: (summary?.rounds_played || 0) >= 1 },
  ];
}

function ChildView({ mastery, summary }) {
  const { profile } = useProfile();
  const badges = badgesFrom(summary, profile);
  return (
    <div className="space-y-5">
      <div>
        <h3 className="font-bold mb-3">Badges</h3>
        <div className="grid grid-cols-2 gap-3">
          {badges.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.id} className="glass rounded-2xl p-3 flex items-center gap-3"
                   style={{ opacity: b.got ? 1 : 0.45 }} data-testid={`badge-${b.id}`}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${b.color}22` }}>
                  <Icon size={20} style={{ color: b.color }} />
                </div>
                <div className="text-sm font-semibold">{b.label}</div>
              </div>
            );
          })}
        </div>
      </div>

      <MasteryGrid grid={mastery.grid} />
    </div>
  );
}

function MasteryGrid({ grid }) {
  return (
    <div>
      <h3 className="font-bold mb-2">Mastery Grid</h3>
      <div className="flex flex-wrap gap-2 mb-3 text-[10px]">
        {Object.entries(STATE_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1 text-[#A8B2D1]">
            <span className="w-3 h-3 rounded-sm inline-block" style={{ background: m.color }} /> {m.label}
          </span>
        ))}
      </div>
      <div className="glass rounded-2xl p-3 overflow-x-auto no-scrollbar" data-testid="mastery-grid">
        <div className="inline-block">
          <div className="flex gap-[3px] mb-[3px] pl-7">
            {MULTS.map((m) => (
              <span key={m} className="w-4 text-[8px] text-center text-[#A8B2D1]">{m}</span>
            ))}
          </div>
          {TABLES.map((t) => (
            <div key={t} className="flex gap-[3px] mb-[3px] items-center">
              <span className="w-6 text-[9px] text-right pr-1 text-[#A8B2D1] font-bold">{t}×</span>
              {MULTS.map((m) => {
                const cell = grid[`${t}x${m}`];
                const meta = STATE_META[cell?.state || "new"];
                return (
                  <span key={m} title={`${t}×${m}`} data-testid={`cell-${t}-${m}`}
                        className="w-4 h-4 rounded-sm shrink-0"
                        style={{ background: meta.color }} />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ParentView({ summary }) {
  if (!summary) return <div className="text-center text-[#A8B2D1] py-8">Loading report…</div>;
  const acc = Math.round(summary.overall_accuracy * 100);
  const avgSec = (summary.avg_response_ms / 1000).toFixed(1);
  const chartData = summary.table_accuracy.map((t) => ({ name: `${t.table}`, acc: Math.round(t.accuracy * 100), attempts: t.attempts }));

  return (
    <div className="space-y-5" data-testid="parent-view">
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Accuracy" value={`${acc}%`} color="#00FF66" />
        <Metric label="Avg time" value={`${avgSec}s`} color="#00E5FF" />
        <Metric label="Rounds" value={summary.rounds_played} color="#FFD600" />
      </div>

      <div className="glass rounded-3xl p-4">
        <h3 className="font-bold mb-1">Accuracy by table</h3>
        <p className="text-[11px] text-[#A8B2D1] mb-3">Tables 2–20. Grey means not practised yet.</p>
        <div style={{ height: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#A8B2D1", fontSize: 10 }} axisLine={false} tickLine={false} interval={0} />
              <Tooltip cursor={{ fill: "rgba(255,255,255,0.05)" }}
                       contentStyle={{ background: "#1D1135", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="acc" radius={[4, 4, 0, 0]}>
                {chartData.map((d, i) => (
                  <Cell key={i} fill={d.attempts === 0 ? "#3B4266" : d.acc >= 80 ? "#00FF66" : d.acc >= 50 ? "#FFD600" : "#FF3366"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="glass rounded-3xl p-4">
        <h3 className="font-bold mb-3">Facts to focus on</h3>
        {summary.weak_facts.length === 0 ? (
          <div className="text-sm text-[#A8B2D1]">No weak facts yet — play a few rounds to see insights.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="weak-facts">
            {summary.weak_facts.map((f, i) => (
              <div key={i} className="rounded-xl px-2.5 py-1.5 text-sm font-math font-bold"
                   style={{ background: `${STATE_META[f.state].color}22`, color: STATE_META[f.state].color }}>
                {f.a}×{f.b} <span className="text-[10px] opacity-80">{Math.round(f.accuracy * 100)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="glass rounded-3xl p-4">
        <h3 className="font-bold mb-3">Learning states</h3>
        <div className="space-y-2">
          {Object.entries(summary.state_counts).map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-sm">
              <span className="w-3 h-3 rounded-sm" style={{ background: STATE_META[k].color }} />
              <span className="flex-1">{STATE_META[k].label}</span>
              <span className="font-bold">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="glass rounded-2xl p-3 text-center">
      <div className="font-black text-2xl" style={{ color }}>{value}</div>
      <div className="text-[11px] text-[#A8B2D1]">{label}</div>
    </div>
  );
}
